import DealerOnboardingApiService, {
  DealerOnboardingProgress,
  OnboardingStepValidationResult,
  SaveDealerOnboardingProgressPayload,
} from 'app/apiService/dealerOnboarding/dealerOnboardingApiService';
import {
  GuidanceTourConfig,
  GuidanceTourStatus,
  GuidanceTourStepCompletionMap,
  GuidanceTourStorageState,
} from '../types';
import {
  getTourStorage as getLocalTourStorage,
  clearTourStorage,
  initStepCompleted,
  mapApiProgressToTourStorage,
} from './tourStorage';

const API_DRIVEN_TOUR_ID = 'dealer-onboarding';

export const isApiDrivenTour = (tourId: string): boolean =>
  tourId === API_DRIVEN_TOUR_ID;

function mapApiProgressToStorage(
  config: GuidanceTourConfig,
  dealerId: string,
  progress: DealerOnboardingProgress,
): GuidanceTourStorageState {
  return mapApiProgressToTourStorage(config, dealerId, {
    status: progress.status,
    currentStepIndex: progress.current_step_index,
    dealerId,
    updatedAt: progress.updated_at
      ? new Date(progress.updated_at).getTime()
      : Date.now(),
    stepCompleted: progress.step_completed,
    stepContext: progress.step_context ?? {},
    stepAccessibility: progress.step_accessibility ?? {},
    lastViewedStepId: progress.last_viewed_step_id,
    hasPausedSession: progress.has_paused_session,
    hasLaunchReadinessIntroAcknowledged: progress.has_launch_readiness_intro_acknowledged,
  });
}

async function migrateLocalStorageIfNeeded(
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
): Promise<GuidanceTourStorageState | null> {
  const local = getLocalTourStorage(tourId, dealerId, config);
  if (!local) {
    return null;
  }

  const hasLocalProgress =
    local.hasPausedSession ||
    Object.values(local.stepCompleted).some(Boolean) ||
    local.currentStepIndex > 0;

  if (!hasLocalProgress) {
    clearTourStorage(tourId, dealerId);
    return null;
  }

  const migrated = await DealerOnboardingApiService.saveProgress(dealerId, {
    status: local.status,
    current_step_index: local.currentStepIndex,
    step_completed: local.stepCompleted,
    last_viewed_step_id: local.lastViewedStepId,
    has_paused_session: local.hasPausedSession,
  });

  clearTourStorage(tourId, dealerId);
  return mapApiProgressToStorage(config, dealerId, migrated);
}

export async function getTourStorageFromApi(
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
): Promise<GuidanceTourStorageState | null> {
  if (!isApiDrivenTour(tourId)) {
    return getLocalTourStorage(tourId, dealerId, config);
  }

  try {
    const progress = await DealerOnboardingApiService.getProgress(dealerId);
    const hasApiProgress =
      progress.has_paused_session ||
      Object.values(progress.step_completed ?? {}).some(Boolean) ||
      progress.current_step_index > 0;

    if (!hasApiProgress) {
      const migrated = await migrateLocalStorageIfNeeded(tourId, dealerId, config);
      if (migrated) {
        return migrated;
      }
    }

    return mapApiProgressToStorage(config, dealerId, progress);
  } catch {
    return getLocalTourStorage(tourId, dealerId, config);
  }
}

export async function saveTourStorageToApi(
  tourId: string,
  dealerId: string,
  state: Partial<GuidanceTourStorageState> & {
    status: GuidanceTourStatus;
    currentStepIndex: number;
  },
  config?: GuidanceTourConfig,
): Promise<GuidanceTourStorageState> {
  if (!isApiDrivenTour(tourId)) {
    const { saveTourStorage } = await import('./tourStorage');
    return saveTourStorage(tourId, dealerId, state, config);
  }

  const existing = await getTourStorageFromApi(tourId, dealerId, config!);
  const payload: SaveDealerOnboardingProgressPayload = {
    status: state.status,
    current_step_index: state.currentStepIndex,
    last_viewed_step_id: state.lastViewedStepId,
    has_paused_session: state.hasPausedSession,
    has_launch_readiness_intro_acknowledged: state.hasLaunchReadinessIntroAcknowledged,
  };

  if (state.stepCompleted) {
    payload.step_completed = {
      ...(existing?.stepCompleted ?? initStepCompleted(config!)),
      ...state.stepCompleted,
    };
  }

  if (state.stepContext) {
    payload.step_context = {
      ...(existing?.stepContext ?? {}),
      ...state.stepContext,
    };
  }

  if ((state as { reset?: boolean }).reset) {
    payload.reset = true;
  }

  const progress = await DealerOnboardingApiService.saveProgress(dealerId, payload);
  return mapApiProgressToStorage(config!, dealerId, progress);
}

export async function resetTourProgressInApi(
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
): Promise<GuidanceTourStorageState> {
  if (!isApiDrivenTour(tourId)) {
    const { resetTourProgress } = await import('./tourStorage');
    return resetTourProgress(tourId, dealerId, config);
  }

  clearTourStorage(tourId, dealerId);
  const progress = await DealerOnboardingApiService.saveProgress(dealerId, { reset: true });
  return mapApiProgressToStorage(config, dealerId, progress);
}

export async function markStepCompletedInApi(
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
  stepId: string,
  currentStepIndex: number,
): Promise<GuidanceTourStorageState> {
  if (!isApiDrivenTour(tourId)) {
    const { markStepCompleted } = await import('./tourStorage');
    return markStepCompleted(
      tourId,
      dealerId,
      config,
      stepId,
      true,
      currentStepIndex,
    );
  }

  const progress = await DealerOnboardingApiService.completeStep(
    dealerId,
    stepId,
    currentStepIndex,
  );
  return mapApiProgressToStorage(config, dealerId, progress);
}

export async function markTourStatusInApi(
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
  status: GuidanceTourStatus,
  currentStepIndex: number,
  stepCompleted?: GuidanceTourStepCompletionMap,
): Promise<GuidanceTourStorageState> {
  return saveTourStorageToApi(
    tourId,
    dealerId,
    {
      status,
      currentStepIndex,
      ...(stepCompleted ? { stepCompleted } : {}),
    },
    config,
  );
}

export async function fetchStepValidation(
  dealerId: string,
  stepId: string,
  options?: {
    crmSupportsEmailReports?: boolean;
    sandboxReadinessRequired?: boolean;
  },
): Promise<OnboardingStepValidationResult> {
  return DealerOnboardingApiService.getStepValidation(dealerId, stepId, options);
}

export async function readLauncherProgressFromApi(
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
) {
  const { getTourProgress, hasSavedTourProgress } = await import('./tourStorage');
  const stored = await getTourStorageFromApi(tourId, dealerId, config);
  const progress = getTourProgress(config, stored);
  const hasProgress = stored
    ? stored.status === 'completed' ||
      (stored.status === 'active' &&
        (Boolean(stored.hasPausedSession) ||
          progress.completed > 0 ||
          stored.currentStepIndex > 0))
    : false;

  return { stored, progress, hasProgress: hasProgress || hasSavedTourProgress(tourId, dealerId, config) };
}
